import { Component, OnDestroy, ViewChild, AfterViewInit, inject } from '@angular/core'
import { Router, ActivatedRoute, ParamMap, Event, NavigationEnd } from '@angular/router'
import { Store, select } from '@ngrx/store'
import { Observable, Subscription, filter, take } from 'rxjs'
import { ShepherdService } from 'angular-shepherd'
import { IconNamesEnum } from 'ngx-bootstrap-icons'
import { BreakpointService } from '@lib/master-detail'
import * as SearchTypes from '../filtering-search-component-base/filtering-search-types'
import { Article, ListingState, removeAllArticles, selectAllArticles, selectCurrentArticle, selectExtraData, selectListingState, selectSearchState, selectStatusState, updateSearchParameters } from '../../../state'
import * as SearchComponent from '../../../shared/components/filtering-search-component-base/filtering-search.component-base'
import { InputDialogComponent, StepState } from '../input-dialog/input-dialog.component'
import { Dictionary } from '@ngrx/entity'
import { BackEndService, ChatMessage } from 'src/app/services/backend.service'
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap'
import { NgScrollbar } from 'ngx-scrollbar'
import { ChatInputMessage } from '../chat-input/chat-input.component'
import { BreadcrumbButtonBarService } from 'src/app/services/breadcrumb-button-bar-service'
import { ButtonConfiguration } from 'src/app/services/button-configuration.interface'

@Component({
    template: '',
    standalone: true
})
export abstract class PaginatedComponentBase<TTItem extends Article> implements AfterViewInit, OnDestroy {
    router = inject(Router)
    route = inject(ActivatedRoute)
    store = inject<Store<TTItem>>(Store)
    breakpointService = inject(BreakpointService)
    protected searchService = inject(BackEndService)
    protected shepherdService = inject(ShepherdService)
    private buttonBarService = inject(BreadcrumbButtonBarService)

    @ViewChild('sendEmailDialog') sendEmailDialogComponent: InputDialogComponent = {} as InputDialogComponent
    @ViewChild('printArticlesDialog') printDialogComponent: InputDialogComponent = {} as InputDialogComponent
    @ViewChild('master') scrollable: NgScrollbar = {} as NgScrollbar


    public iconNames = IconNamesEnum
    public searchTypes = SearchTypes

    public routeChangeSub$: Subscription = Subscription.EMPTY
    public selectedArticle$: Observable<TTItem> = new Observable()
    public articles$: Observable<TTItem[]>
    public listing$: Observable<ListingState>
    public extraData$: Observable<any>
    public listingSubs$: Subscription = Subscription.EMPTY
    public search$: Observable<any>
    public status$: Observable<any>

    public searchOptionGroups: SearchComponent.SearchOptionGroup[] = []
    public selectedOptions: SearchComponent.NamedSearchParameters = {}
    public requiredFilters: Dictionary<any> = {}

    public selectedArticleLimit = 10
    public selectionTypeInProgress = ''
    public selectedArticles: Dictionary<TTItem> = {}
    public selectedArticleCount = 0
    public showCheckboxes = false
    public shownPopover = false
    public actionProgress = 100

    public contentSet = ''
    public linkBase = ''
    public initialSearch = {}

    public listItemViewController
    public overviewViewController

    public shareEmailAddresses = ''
    public shareEmailMessage = ''

    public useAIsearch = false

    constructor() {
        this.articles$ = this.store.select(selectAllArticles) as unknown as Observable<TTItem[]>
        this.listing$ = this.store.select(selectListingState)
        this.search$ = this.store.select(selectSearchState)
        this.status$ = this.store.select(selectStatusState)
        this.selectedArticle$ = this.store.pipe(select(selectCurrentArticle()))
        this.extraData$ = this.store.select(selectExtraData)

        this.listItemViewController = this.route.snapshot.data['listComponent']
        this.overviewViewController = this.route.snapshot.data['overviewComponent']
        this.searchOptionGroups = SearchTypes.initSearchOptionGroup(structuredClone(this.route.snapshot.data['searchOptionGroups']))
        this.requiredFilters = structuredClone(this.route.snapshot.data['requiredFilters'])
        this.contentSet = this.route.snapshot.data['contentSet']
        this.linkBase = this.route.snapshot.data['linkBase'] ?? this.contentSet
        this.initialSearch = this.route.snapshot.data['search']

        this.useAIsearch = this.route.snapshot.data['useAIsearch'] ?? false
    }

    ngOnInit(): void {
        if (this.useAIsearch) {
            this.store.dispatch(removeAllArticles())

            this.store.dispatch(updateSearchParameters({
                area: this.contentSet,
                order: [],
                parameters: {
                    chatQuery: {
                        value: {
                            cleanText: "show all",
                            htmlContent: "show all",
                            elements: []
                        }
                    }
                }
            }))

            const newButton: ButtonConfiguration = {
                label: ['AI search', 'Manual search'],
                clickAction: (label:string) => {
                    if (label === 'AI search') {
                        this.useAIsearch = true
                    } else {
                        this.useAIsearch = false
                    }
                },
            }
            this.buttonBarService.addButton(newButton)

        }
    }

    protected scrollToElement(id: number) {
        if (id && document.querySelector('#article_' + id)) {
            this.scrollable?.scrollToElement('#article_' + id!, {
                top: -40,
                //duration: 1000
            })
        }
    }

    ngAfterViewInit() {
        this.listingSubs$ = this.listing$.subscribe((listing) => {
            if (listing.present === 0) {
                //  If we add this back we get https://angular.io/errors/NG0100
                this.breakpointService.setExpandListing(true)
            }
        })
        if (this.route.firstChild) {
            this.routeChangeSub$ = this.route.firstChild.paramMap
                .subscribe((map: ParamMap) =>
                    this.getRouteParams(map))
        } else {
            this.routeChangeSub$ = this.router.events.pipe(
                filter((e: Event) => e instanceof NavigationEnd)
            ).subscribe((e: any) => {
                const lastUrlPart = parseInt(e.url.split('/').pop() ?? '')
                if (lastUrlPart > 0) {
                    this.breakpointService.setExpandListing(false)
                    this.scrollToElement(lastUrlPart)
                } else {
                    this.breakpointService.setExpandListing(true)
                }
            })
        }
        if (Array.isArray(this.route.snapshot.data['shepherdIntro'])) {
            this.setupShepherd(this.route.snapshot.data['shepherdIntro'])
        }
    }

    ngOnDestroy() {
        this.routeChangeSub$.unsubscribe()
        this.listingSubs$.unsubscribe()
    }

    loadMore(listing: any) {
        if (listing.present < listing.total) {
            this.store.dispatch({ type: '[Articles] Get Articles', area: this.contentSet })
        }
    }

    setupShepherd(shepherdIntro: any[]) {
        const [defaultStepOptions, defaultSteps] = shepherdIntro
        this.shepherdService.defaultStepOptions = defaultStepOptions
        this.shepherdService.modal = true
        this.shepherdService.confirmCancel = false
        this.shepherdService.addSteps(defaultSteps)
    }

    getRouteParams(map: ParamMap): number {
        const articleID = map.get('id')
        let id: number = -1
        if (articleID) {
            this.breakpointService.setExpandListing(false)
            this.scrollToElement(parseInt(articleID))
        }
        return id
    }

    startSelection(type: string) {
        if (type == 'download') {
            let listing: any = {}
            this.listing$.subscribe((l) => listing = l)
            this.selectedArticleLimit = Math.min(listing.total, 1000)
        } else {
            this.selectedArticleLimit = 10
        }
        this.showCheckboxes = true
        this.selectionTypeInProgress = type
        this.shownPopover = false
        this.selectedArticles = {}
        this.selectedArticleCount = 0
        this.shareEmailAddresses = ''

        let selectedArticle: TTItem | undefined = undefined
        let tmpSub = this.selectedArticle$.subscribe((article: TTItem) => {
            selectedArticle = article
        })
        if (selectedArticle) {
            this.selectedArticles[(selectedArticle as TTItem).id] = selectedArticle
            this.selectedArticleCount = 1
        }
        tmpSub.unsubscribe()
    }

    selectedArticlesLimitReached(checkbox: HTMLInputElement): boolean {
        return !checkbox.checked && this.selectedArticleCount === this.selectedArticleLimit
    }


    changeSelection(article: TTItem, event: any) {
        if (event.target.checked) {
            this.selectedArticles[article.id] = article
        } else {
            delete this.selectedArticles[article.id]
        }
        this.selectedArticleCount = Object.keys(this.selectedArticles).length
    }

    changeAllSelection(event: any) {
        if (event.target.checked) {
            this.articles$.pipe(take(1)).subscribe((articles: TTItem[]) => {
                this.selectedArticles = {}
                articles.forEach((article: TTItem) => {
                    this.selectedArticles[article.id] = article
                })
            })
        } else {
            this.selectedArticles = {}
        }
        this.selectedArticleCount = Object.keys(this.selectedArticles).length
    }

    indeterminateNumberChecked(present: number): boolean {
        return this.selectedArticleCount > 0 && this.selectedArticleCount != present
    }

    selectedAllChecked(present: number): boolean {
        return this.selectedArticleCount === present
    }

    finishAction(type: string) {
        if (this.selectedArticleCount > 0) {
            if (type === 'email') {
                this.sendEmailDialogComponent.showModal(this.selectedArticles)
            } else {
                if (!this.printDialogComponent) {
                    window.open(`/print/${this.contentSet}?ids=${Object.keys(this.selectedArticles).join(',')}`, "_blank")
                    this.cancelCurrentAction()
                }
            }
        } else {
            this.cancelCurrentAction()
        }
    }

    cancelCurrentAction() {
        this.showCheckboxes = false
        this.shownPopover = false
        this.selectedArticles = {}
        this.selectedArticleCount = 0
        this.selectionTypeInProgress = ''
        this.shareEmailMessage = ''
    }

    sendEmailStage(returned: StepState) {
        if (returned.step === 2) {
            const self = this

            this.searchService.shareByEmail(this.contentSet, this.shareEmailAddresses.split(',').map((email) => email.trim()), this.shareEmailMessage, Object.keys(this.selectedArticles))
                .subscribe(
                    (response: any) => {
                        self.sendEmailDialogComponent.advanceToNextStep()
                        self.cancelCurrentAction()
                    }
                )
        }
    }

    checkEmailAddress() {
        return true
    }

    expandListing(extras: string[] = []) {
        this.breakpointService.setExpandListing(true)
        this.selectedArticle$ = new Observable()
        this.router.navigate([this.linkBase ?? this.contentSet, ...extras])
    }

    trackbyArticleID(index: number, a: TTItem) {
        return a.id
    }

    showPopover(popover: NgbPopover) {
        if (!this.shownPopover && this.showCheckboxes && this.selectedArticleCount === 0) {
            this.shownPopover = true
            popover.open()
        }
        return true
    }

    onSendChatMessage(message: ChatInputMessage): void {
        // Handle sending chat message logic here
        console.log('Chat message sent:', message);
        // You can also implement AI search functionality here if needed
        this.store.dispatch(removeAllArticles())

        this.store.dispatch(updateSearchParameters({
            area: this.contentSet,
            order: [],
            parameters: {
                chatQuery: { value: message },
            }
        }))
    }

}
